// Installs a global fetch interceptor that attaches the current Keycloak
// access token to every request to this app's backend. The backend now
// verifies that token on every /api/* route (see server.cjs verifyToken
// middleware) instead of trusting client-supplied role fields, so without
// this every API call from anywhere in the app would start failing with 401.
//
// This wraps window.fetch once, at app startup, rather than editing every
// individual fetch()/apiCall() call site (there are 100+ scattered across
// components) — any call whose URL contains "/api/" gets the header added
// automatically, whether it uses a relative path (proxied by Vite in dev)
// or an absolute http://localhost:5000/api/... URL.
import keycloak from '../config/keycloak.js';
import { API_BASE_URL } from '../config/api';

// Only our own backend gets the bearer token — relative /api paths (Vite
// proxy / same origin), same-origin absolute URLs, or the configured backend.
const isOwnApi = (url: string): boolean => {
  if (!url.includes('/api/')) return false;
  if (url.startsWith('/') && !url.startsWith('//')) return true;
  try {
    const origin = new URL(url, window.location.href).origin;
    return origin === window.location.origin || origin === new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
};

let installed = false;

export function installAuthFetch() {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

    if (isOwnApi(url) && keycloak?.authenticated) {
      // Keycloak access tokens are short-lived (~5 min). Nothing else in the
      // app refreshes them proactively, so a token that expired while the tab
      // sat idle would otherwise be sent as-is and get a 401 from the backend
      // — surfacing as blank/failed profile loads that look like the account
      // itself lost its data. Refresh here (no-op if >30s from expiry).
      try {
        await keycloak.updateToken(30);
      } catch (err) {
        console.error('Keycloak token refresh failed:', err);
      }

      // Always overwrite Authorization with the freshly refreshed token: many
      // call sites build `Bearer ${keycloak.token}` themselves at render time,
      // which can be the stale pre-refresh token by the time the request runs.
      const existingHeaders = init?.headers ?? (typeof input !== 'string' && !(input instanceof URL) ? input.headers : undefined);
      const headers = new Headers(existingHeaders);
      if (keycloak.token) {
        headers.set('Authorization', `Bearer ${keycloak.token}`);
      }
      init = { ...init, headers };
    }

    return originalFetch(input, init);
  };
}
